// Paging the Email Briefing's lists (Saffet 2026-10-28: "Emails just shows about 30 when I have
// over 1000", "50 email groups").
//
// Every list behind a number on the Email card used to be ONE request of 50 rows and nothing after
// it, and "N email groups" was that page's length. The rule now: the number on a row is the total
// the server states for its list, and the list pages through all of it. These are the pure halves
// of that — the store asks, these decide what the answer means and what to ask next.

/** How many rows one page asks for — the frame's page, and the BFF's per-call cap is 100. */
export const LIST_PAGE_SIZE = 50

/** `{ rows, total }` out of a Briefing thread answer (`GET v1/email/briefing/threads`). */
export function readThreadsPage(data) {
  const rows = Array.isArray(data?.rows) ? data.rows.filter(Boolean) : []
  const total = Number.isFinite(Number(data?.total)) && data?.total != null ? Math.max(0, Number(data.total)) : rows.length
  return { rows, total }
}

/**
 * `{ groups, total }` out of a sender-groups answer. `POST v1/email/groups/page` states the true
 * total; the bare list `POST v1/email/groups` (a BFF older than the page route) does not, and there
 * the list is all that is known — its length is the honest fallback.
 */
export function readGroupsPage(data) {
  if (Array.isArray(data)) return { groups: data.filter(Boolean), total: null }
  const groups = Array.isArray(data?.groups) ? data.groups.filter(Boolean) : []
  const total = data?.total != null && Number.isFinite(Number(data.total)) ? Math.max(0, Number(data.total)) : null
  return { groups, total }
}

/**
 * `{ items, total }` out of one sender group's threads — `POST v1/email/groups/sender-threads`
 * (`{ items, total }`), or the timeline listing it replaced (`{ items, totalCount }` / a bare list).
 */
export function readSenderThreadsPage(data) {
  const items = Array.isArray(data) ? data : (data?.items || data?.data || [])
  const list = Array.isArray(items) ? items.filter(Boolean) : []
  const raw = Array.isArray(data) ? null : (data?.total ?? data?.totalCount)
  const total = raw != null && Number.isFinite(Number(raw)) ? Math.max(0, Number(raw)) : list.length
  return { items: list, total }
}

/** Is there more of a list to fetch? Only when the server said how much there is, and more of it. */
export function hasMore(loaded, total) {
  const n = Array.isArray(loaded) ? loaded.length : Number(loaded) || 0
  return total != null && Number(total) > n
}

/**
 * The key a row of any Email list is known by: a Briefing row's `timelineId` (it has no `id`), else
 * the timeline item's own `id`. Empty for a row that carries neither.
 */
export const rowKey = r => r?.timelineId || r?.id || ''

/**
 * The loaded rows followed by the next page, each row once (a row that moved between pages is kept
 * where it was first seen). A row with no key cannot be told apart from another, so it is never
 * taken for a repeat — dropping it would lose a row the server counted.
 */
export function appendPage(existing, more, keyOf = rowKey) {
  const out = [...(existing || []).filter(Boolean)]
  const seen = new Set(out.map(keyOf).filter(k => k != null && k !== ''))
  for (const row of more || []) {
    if (!row) continue
    const key = keyOf(row)
    const known = key != null && key !== ''
    if (known && seen.has(key)) continue
    if (known) seen.add(key)
    out.push(row)
  }
  return out
}

/**
 * What one sender group's threads are asked by: its ADDRESS (the server matches it exactly) and the
 * stored sender as the fallback key. Null for a group with neither.
 */
export function senderThreadsRequest(group, skip = 0, take = LIST_PAGE_SIZE) {
  const emailAddress = group?.emailAddress || ''
  const sender = group?.sender || ''
  if (!emailAddress && !sender) return null
  return { emailAddress, sender, skip: Math.max(0, Number(skip) || 0), take }
}

/**
 * How many threads opening a sender group lists — the number its row states. `threadCount` is that
 * exact number; a BFF older than it only knows `totalCount` (emails), which is the best it has.
 */
export function groupSize(group) {
  const threads = Number(group?.threadCount) || 0
  return threads > 0 ? threads : (Number(group?.totalCount) || 0)
}
